// src/pages/reports/CallCenterSummaryPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Phone,
  PlusSquare,
  CheckCircle,
  Truck,
  Check,
  Undo2,
  Archive,
  Percent,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import FilterPanel from '../../components/common/FilterPanel/FilterPanel';
import ReportDatePresetFilter from '../../components/common/ReportDatePresetFilter/ReportDatePresetFilter';
import StatsCards from '../../components/common/StatsCards/StatsCards';
import DataTable from '../../components/common/DataTable/DataTable';
import Gauge from '../../components/common/Gauge/Gauge';
import InfoIcon from '../../components/common/InfoIcon/InfoIcon';
import {
  getCallCenterSummaryKpis,
  getCallCenterTodayAchievement,
  getCallCenterLast7DaysTrend,
  getCallCenterCrossSellProducts,
  getCallCenterTargetPerAgent,
  getCallCenterAgentPerformance,
} from '../../services/api/callCenterSummaryReport';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency, formatCurrencyCompact } from '../../utils/currencyUtil';
import styles from './CallCenterSummaryPage.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const alignedHeader = (text) => (
  <span style={{ display: 'block', width: '100%', textAlign: 'right' }}>{text}</span>
);

const pad = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    dateFrom: fmt(from),
    dateTo: fmt(today),
  };
};

const buildAchievedVsTargetChartData = (series, t) => ({
  labels: series.map((s) => s.label),
  datasets: [
    { label: t('report.achieved'), data: series.map((s) => s.achieved), backgroundColor: '#1e40af' },
    { label: t('report.target'), data: series.map((s) => s.target), backgroundColor: '#93c5fd' },
  ],
});

const achievedVsTargetChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 900, easing: 'easeOutQuart' },
  plugins: {
    legend: { position: 'top' },
    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}` } },
  },
  scales: {
    y: { beginAtZero: true, title: { display: true, text: 'LKR' } },
    x: { title: { display: true, text: 'Date' } },
  },
};

const CallCenterSummaryPage = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({ date: getDefaultDateRange() });

  const [kpis, setKpis] = useState(null);
  const [crossSellProducts, setCrossSellProducts] = useState([]);
  const [targetPerAgent, setTargetPerAgent] = useState([]);
  const [isLoadingFiltered, setIsLoadingFiltered] = useState(false);

  const [agentPerformance, setAgentPerformance] = useState({ agents: [], total: 0 });
  const [agentPage, setAgentPage] = useState({ currentPage: 1, pageSize: 20 });
  const [isLoadingAgentPerformance, setIsLoadingAgentPerformance] = useState(false);

  const [todayAchievement, setTodayAchievement] = useState(null);
  const [last7DaysTrend, setLast7DaysTrend] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(true);

  // Effect B — fixed context widget (trailing 7 days), runs once on mount only
  useEffect(() => {
    loadTrendWidgets();
  }, []);

  // Effect A — page-filtered widgets, re-runs whenever the date filter changes
  useEffect(() => {
    if (filters.date?.dateFrom && filters.date?.dateTo) {
      loadFilteredWidgets();
      setAgentPage((p) => ({ ...p, currentPage: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date?.dateFrom, filters.date?.dateTo]);

  // Separate effect for the paginated Agent Performance table
  useEffect(() => {
    if (filters.date?.dateFrom && filters.date?.dateTo) {
      loadAgentPerformance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date?.dateFrom, filters.date?.dateTo, agentPage.currentPage, agentPage.pageSize]);

  const loadTrendWidgets = async () => {
    try {
      setIsLoadingTrend(true);
      const trend = await getCallCenterLast7DaysTrend();
      setLast7DaysTrend(trend.data || []);
    } catch (err) {
      showError(err.response?.data?.message || t('report.callCenterSummary.failedToLoad'));
    } finally {
      setIsLoadingTrend(false);
    }
  };

  const loadFilteredWidgets = async () => {
    try {
      setIsLoadingFiltered(true);
      const params = { dateFrom: filters.date.dateFrom, dateTo: filters.date.dateTo };
      const [kpiData, crossSell, targets, achievement] = await Promise.all([
        getCallCenterSummaryKpis(params),
        getCallCenterCrossSellProducts(params),
        getCallCenterTargetPerAgent(params),
        getCallCenterTodayAchievement(params),
      ]);
      setTodayAchievement(achievement);
      setKpis(kpiData);
      setCrossSellProducts(crossSell.products || []);
      setTargetPerAgent(targets.agents || []);
    } catch (err) {
      showError(err.response?.data?.message || t('report.callCenterSummary.failedToLoad'));
    } finally {
      setIsLoadingFiltered(false);
    }
  };

  const loadAgentPerformance = async () => {
    try {
      setIsLoadingAgentPerformance(true);
      const res = await getCallCenterAgentPerformance({
        dateFrom: filters.date.dateFrom,
        dateTo: filters.date.dateTo,
        page: agentPage.currentPage,
        limit: agentPage.pageSize,
      });
      setAgentPerformance({ agents: res.agents || [], total: res.total || 0 });
    } catch (err) {
      showError(err.response?.data?.message || t('report.callCenterSummary.failedToLoad'));
    } finally {
      setIsLoadingAgentPerformance(false);
    }
  };

  const handleFilterChange = (values) => {
    setFilters((prev) => ({ ...prev, ...values }));
  };

  const handleFilterClear = () => {
    setFilters({ date: getDefaultDateRange() });
  };

  const filterDefs = [
    {
      key: 'date',
      label: t('report.dateRange'),
      type: 'custom',
      gridColumn: '1 / -1',
      render: (value, onChange) => <ReportDatePresetFilter value={value || {}} onChange={onChange} />,
    },
  ];

  const buildKpiStats = () => {
    if (!kpis) return [];
    const bucketStat = (key, label, tooltip, icon, color) => ({
      key,
      label,
      tooltip,
      icon,
      color,
      value: kpis[key].qty,
      format: (v) => {
        const compact = formatCurrencyCompact(kpis[key].value);
        return (
          <div className={styles.cardValueStack}>
            <div>{v.toLocaleString()}</div>
            <div className={styles.cardSubValue} title={compact.title}>LKR {compact.display}</div>
          </div>
        );
      },
    });

    return [
      bucketStat('created', t('report.callCenterSummary.createdOrderItems'), t('report.callCenterSummary.createdOrderItemsTooltip'), PlusSquare, '#f59e0b'),
      bucketStat('valid', t('report.callCenterSummary.validOrderItems'), t('report.callCenterSummary.validOrderItemsTooltip'), CheckCircle, '#3b82f6'),
      bucketStat('delivered', t('report.callCenterSummary.deliveredOrderItems'), t('report.callCenterSummary.deliveredOrderItemsTooltip'), Truck, '#ef4444'),
      bucketStat('settled', t('report.callCenterSummary.settledOrderItems'), t('report.callCenterSummary.settledOrderItemsTooltip'), Check, '#8b5cf6'),
      bucketStat('inCourier', t('report.callCenterSummary.inCourier'), t('report.callCenterSummary.inCourierTooltip'), Truck, '#06b6d4'),
      bucketStat('returns', t('report.callCenterSummary.returns'), t('report.callCenterSummary.returnsTooltip'), Undo2, '#dc2626'),
      bucketStat('other', t('report.callCenterSummary.otherOrderItems'), t('report.callCenterSummary.otherOrderItemsTooltip'), Archive, '#6b7280'),
      {
        key: 'discount',
        label: t('report.callCenterSummary.discount'),
        tooltip: t('report.callCenterSummary.discountTooltip'),
        icon: Percent,
        color: '#f59e0b',
        value: kpis.discount,
        format: (v) => formatCurrency(v),
      },
    ];
  };

  const crossSellColumns = [
    { key: 'productName', header: t('report.callCenterSummary.product') },
    { key: 'qty', header: alignedHeader(t('report.callCenterSummary.count')), align: 'right', render: (row) => row.qty.toLocaleString() },
    { key: 'revenue', header: alignedHeader(t('report.callCenterSummary.revenue')), align: 'right', render: (row) => formatCurrency(row.revenue) },
  ];

  const targetPerAgentColumns = [
    { key: 'userName', header: t('report.callCenterSummary.name') },
    { key: 'target', header: alignedHeader(t('report.target')), align: 'right', render: (row) => formatCurrency(row.target) },
    { key: 'achieved', header: alignedHeader(t('report.achieved')), align: 'right', render: (row) => formatCurrency(row.achieved) },
    {
      key: 'remaining',
      header: alignedHeader(t('report.callCenterSummary.remaining')),
      align: 'right',
      render: (row) => (row.remaining <= 0
        ? <span className={styles.targetAchieved}>{t('report.callCenterSummary.targetAchieved')}</span>
        : formatCurrency(row.remaining)),
    },
  ];

  const agentPerformanceColumns = [
    { key: 'rank', header: t('report.callCenterSummary.rank'), width: '80px' },
    { key: 'userName', header: t('report.callCenterSummary.name') },
    { key: 'confirmCount', header: alignedHeader(t('report.callCenterSummary.confirmCount')), align: 'right', render: (row) => row.confirmCount.toLocaleString() },
    { key: 'totalSales', header: alignedHeader(t('report.callCenterSummary.sales')), align: 'right', render: (row) => formatCurrency(row.totalSales) },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('report.callCenterSummary.title')}
        description={t('report.callCenterSummary.subtitle')}
        icon={Phone}
      />

      <FilterPanel
        filters={filterDefs}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
        show={showFilters}
        onToggle={() => setShowFilters((s) => !s)}
      />

      <div className={styles.sectionHeader}>
        <h2>{t('report.callCenterSummary.kpiSectionTitle')}</h2>
        <InfoIcon text={t('report.callCenterSummary.kpiSectionTooltip')} />
      </div>
      <StatsCards
        loading={isLoadingFiltered && !kpis}
        stats={buildKpiStats()}
        minColumnWidth="220px"
      />

      <div className={styles.trendGrid}>
        <div className={styles.gaugeCard}>
          <div className={styles.cardHeader}>
            <h3>{t('report.callCenterSummary.todayAchievement')}</h3>
            <InfoIcon text={t('report.callCenterSummary.todayAchievementTooltip')} />
          </div>
          <div className={styles.achievementBody}>
            <div className={styles.achievementDetails}>
              <p><strong>{t('report.callCenterSummary.products')}:</strong> {(todayAchievement?.products || 0).toLocaleString()}</p>
              <p><strong>{t('report.callCenterSummary.orderValue')}:</strong> {formatCurrency(todayAchievement?.value || 0)}</p>
              <p><strong>{t('report.callCenterSummary.targetValue')}:</strong> {formatCurrency(todayAchievement?.target || 0)}</p>
              <p><strong>{t('report.callCenterSummary.discount')}:</strong> {formatCurrency(todayAchievement?.discount || 0)}</p>
              <p><strong>{t('report.callCenterSummary.commission')}:</strong> {formatCurrency(todayAchievement?.commission || 0)}</p>
            </div>
            <Gauge
              value={todayAchievement?.achieved || 0}
              target={todayAchievement?.target || 0}
              label={t('report.callCenterSummary.periodLabel')}
              formatValue={(v) => formatCurrency(v)}
            />
          </div>
        </div>

        <div className={styles.chartCard}>
          <div className={styles.cardHeader}>
            <h3>{t('report.callCenterSummary.last7DaysTitle')}</h3>
            <InfoIcon text={t('report.callCenterSummary.last7DaysTooltip')} />
          </div>
          <div className={styles.chartWrapper}>
            <Bar data={buildAchievedVsTargetChartData(last7DaysTrend, t)} options={achievedVsTargetChartOptions} />
          </div>
        </div>
      </div>

      <div className={styles.tablesGrid}>
        <section className={styles.tableSection}>
          <div className={styles.cardHeader}>
            <h2>{t('report.callCenterSummary.crossSellingTitle')}</h2>
            <InfoIcon text={t('report.callCenterSummary.crossSellingTooltip')} />
          </div>
          <DataTable
            data={crossSellProducts}
            columns={crossSellColumns}
            loading={isLoadingFiltered}
            emptyMessage={t('report.noDataAvailable')}
          />
        </section>

        <section className={styles.tableSection}>
          <div className={styles.cardHeader}>
            <h2>{t('report.callCenterSummary.targetPerAgentTitle')}</h2>
            <InfoIcon text={t('report.callCenterSummary.targetPerAgentTooltip')} />
          </div>
          <DataTable
            data={targetPerAgent}
            columns={targetPerAgentColumns}
            loading={isLoadingFiltered}
            emptyMessage={t('report.noDataAvailable')}
          />
        </section>

        <section className={styles.tableSection}>
          <div className={styles.cardHeader}>
            <h2>{t('report.callCenterSummary.agentPerformanceTitle')}</h2>
            <InfoIcon text={t('report.callCenterSummary.agentPerformanceTooltip')} />
          </div>
          <DataTable
            data={agentPerformance.agents}
            columns={agentPerformanceColumns}
            loading={isLoadingAgentPerformance}
            pagination={{
              currentPage: agentPage.currentPage,
              pageSize: agentPage.pageSize,
              total: agentPerformance.total,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onPaginationChange={(p) => setAgentPage({ currentPage: p.currentPage, pageSize: p.pageSize })}
            emptyMessage={t('report.noDataAvailable')}
          />
        </section>
      </div>
    </div>
  );
};

export default CallCenterSummaryPage;
